import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { IModuleSchema, ModuleService } from '@/api/module';
import { IProjectSchema, ProjectService } from '@/api/project';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { useAccess } from '@/utils/hooks/useAccess';
import { useIsExternal } from '@/utils/hooks/useUserType';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useParams } from 'react-router-dom';
import { CardModule, CardModuleContext } from '@/components/module/CardModule';
import {
  init,
  removeSelectedModule,
  resetSelectedModule,
  setSelectedModule,
} from '@/states/moduleSlice';
import { useModuleDispatch, useModuleSelector } from '@/states/moduleHook';
import moduleStore from '@/states/moduleStore';
import { Provider } from 'react-redux';
import { AddModuleOrSubmodule } from '@/components/module/AddModuleOrSubModule';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { FaArchive } from 'react-icons/fa';

type IListModuleContext = {
  isViewer?: boolean;
  project?: IProjectSchema;
};

export const ListModuleContext = createContext<IListModuleContext>({});

export const ListModule: React.FC = () => {
  return (
    <Provider store={moduleStore}>
      <ListModuleContent />
    </Provider>
  );
};

const ListModuleContent: React.FC = () => {
  const { id } = useParams();
  const isViewer = useAccess(id!);
  const isExternal = useIsExternal();
  const isReadOnly = isViewer || isExternal;

  const navigate = useNavigate();
  const dispatch = useModuleDispatch();
  const selector = useModuleSelector(state => state);

  const { data: project, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const {
    data: moduleData,
    refetch,
    isFetched: isModuleFetched,
    isFetching: isModuleFetching,
  } = ModuleService.useGetAll({ projectId: id, isArchive: false });

  const isFetching = isProjectFetching || isModuleFetching;

  const contextValue = useMemo(
    () => ({
      isViewer: isReadOnly,
      project,
    }),
    [isReadOnly, project],
  );

  useEffect(() => {
    // Hanya jalankan init ketika moduleData dan project telah di-fetch
    if (isModuleFetched && project) {
      dispatch(init({ modules: moduleData ?? [], project }));
    }
  }, [isModuleFetched, project, moduleData, dispatch]);

  const handleSelectModule = useCallback(
    (module: IModuleSchema, cardIndex?: number) => {
      if (cardIndex === undefined) {
        dispatch(resetSelectedModule());
        dispatch(setSelectedModule({ module, level: 0 }));
      } else {
        dispatch(setSelectedModule({ module, level: cardIndex + 1 }));
      }
    },
    [dispatch],
  );

  const handleCloseModule = useCallback(
    (cardIndex: number) => {
      dispatch(removeSelectedModule({ level: cardIndex }));
    },
    [dispatch],
  );

  const toArchive = () => navigate('archive');

  const cardModuleContextValue = useMemo(
    () => ({
      cardIndex: 0,
      onSelectedModule: handleSelectModule,
    }),
    [handleSelectModule],
  );

  const moduleList = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(selector.selectedModule).map(([_, module], index) => ({
        key: module.id,
        value: {
          cardIndex: index + 1,
          onSelectedModule: (module: IModuleSchema) =>
            handleSelectModule(module, index),
          onClose: () => handleCloseModule(index),
        },
        module,
      })),
    [selector.selectedModule, handleSelectModule, handleCloseModule],
  );

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={`All Epic List - ${project?.title}`}
      actions={
        !isReadOnly ? (
          <div className="flex flex-row gap-3">
            <OutlineButton icon={<FaArchive />} onClick={toArchive}>
              Archive
            </OutlineButton>
            <AddModuleOrSubmodule project={project!} onSuccess={refetch} />
          </div>
        ) : (
          <></>
        )
      }
    >
      <ListModuleContext.Provider value={contextValue}>
        <div className="min-h-screen overflow-x-auto">
          <div className="flex flex-row gap-2.5 w-max">
            <DndProvider backend={HTML5Backend}>
              <CardModuleContext.Provider value={cardModuleContextValue}>
                <CardModule
                  title={`Epic (${moduleData?.length})`}
                  onSuccess={refetch}
                />
              </CardModuleContext.Provider>

              {moduleList.map(({ key, value, module }) => (
                <CardModuleContext.Provider key={key} value={value}>
                  <CardModule
                    title={module.module_name}
                    parent={module}
                    onSuccess={refetch}
                  />
                </CardModuleContext.Provider>
              ))}
            </DndProvider>
          </div>
        </div>
      </ListModuleContext.Provider>
    </Section>
  );
};
